import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import axios from 'axios';

const Layout = () => {
  const { logout } = useContext(AuthContext);
  const [accessLevel, setAccessLevel] = useState(localStorage.getItem('accessLevel'));
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [profilePhoto, setProfilePhoto] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/') navigate('/home');

    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getIndustries`)
      .then(res => setIndustries(res.data.industries))
      .catch(err => console.error('Error fetching industries', err));

    if (token) {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/profile/get`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => 
          {setProfilePhoto(res.data.profile_photo)
          localStorage.setItem('isprovider',res.data.isprovider? "yes":"no")}
        )
        .catch(err => console.error('Error fetching profile photo:', err));
    }
  }, [location.pathname]);

  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleLogout = () => {
    logout();
    window.location.href = "/";
  };

  const drawerItems = (
    <Box sx={{ width: 250 }} role="presentation" onClick={() => setDrawerOpen(false)}>
      <List>
        <ListItem button onClick={() => navigate('/home')}>
          <ListItemText primary="Home" />
        </ListItem>
        <ListItem button onClick={() => navigate('/companies')}>
          <ListItemText primary="Industry presence" />
        </ListItem>
        <ListItem button onClick={() => navigate('/opportunities')}>
          <ListItemText primary="Opportunities" />
        </ListItem>
        <ListItem button onClick={() => navigate('/events')}>
          <ListItemText primary="Events" />
        </ListItem>
        <ListItem button onClick={() => navigate('/request')}>
          <ListItemText primary="Request" />
        </ListItem>
        <ListItem button onClick={() => navigate('/about')}>
          <ListItemText primary="About" />
        </ListItem>
        {accessLevel === 'admin' && (
          <ListItem button onClick={() => navigate('/admin')}>
            <ListItemText primary="Admin" />
          </ListItem>
        )}
      </List>
      <Divider />
      <List>
        <ListItem button onClick={() => navigate(`/profile-update`)}>
          <ListItemText primary="Profile Update" />
        </ListItem>
        <ListItem button onClick={() => navigate(`/updatepassword`)}>
          <ListItemText primary="Password Update" />
        </ListItem>
        <ListItem button onClick={handleLogout}>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <AppBar position="sticky" elevation={3} sx={{ backgroundColor: '#385170' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Mobile: Hamburger Menu */}
          {isMobile ? (
            <>
              <IconButton color="inherit" onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                UDYOG Connect
              </Typography>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button color="inherit" onClick={() => navigate('/home')}>
                <Typography variant="h6">UDYOG Connect</Typography>
              </Button>
              <Button color="inherit" onClick={() => navigate('/companies')}>
                  Industry presence
              </Button>
              <Button color="inherit" onClick={() => navigate('/opportunities')}>
                Opportunities
              </Button>
              <Button color="inherit" onClick={() => navigate('/events')}>
                Events
              </Button>
              <Button color="inherit" onClick={() => navigate('/request')}>
                Request
              </Button>
              <Button color="inherit" onClick={() => navigate('/about')}>
                About
              </Button>
              {accessLevel === 'admin' && (
                <Button color="inherit" onClick={() => navigate('/admin')}>
                  Admin
                </Button>
              )}
            </Box>
          )}

          {/* Profile Icon */}
          <IconButton color="inherit" onClick={handleUserMenuOpen}>
            {profilePhoto ? (
              <img
                src={profilePhoto}
                alt="profile"
                style={{ width: 32, height: 32, borderRadius: '50%' }}
              />
            ) : (
              <AccountCircle />
            )}
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Drawer for Mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {drawerItems}
      </Drawer>

      {/* Help Menu */}
      <Menu
        id="help-menu"
        anchorEl={helpAnchorEl}
        open={Boolean(helpAnchorEl)}
        onClose={() => setHelpAnchorEl(null)}
        onMouseLeave={() => setHelpAnchorEl(null)}
      >
        {industries.length > 0 ? (
          industries.map((industry) => (
            <MenuItem
              key={industry}
              onClick={() => navigate(`/companies?industry=${industry}`)}
              sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              {industry}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No Industries Found</MenuItem>
        )}
      </Menu>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
      >
        <MenuItem onClick={() => navigate(`/profile-update`)}>Profile Update</MenuItem>
        <MenuItem onClick={() => navigate(`/updatepassword`)}>Password Update</MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      {/* Main Content */}
      <Box sx={{ p: { xs: 1, sm: 3 } }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
